import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Badge, Button, Card, Page, PageHeader, Table } from '@evogard/ui';
import { api, ok } from '../../lib/api.js';

const format = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

export function SessionsScreen() {
  const client = useQueryClient();
  // Encerrar sessão não tem volta. A confirmação é aqui, na própria linha:
  // diálogo modal é quase sempre preguiça, e para uma linha de tabela é sempre.
  const [confirming, setConfirming] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['plataforma', 'sessoes'],
    queryFn: async () => {
      const r = await api.platform.listMySessions();
      return ok(r, 200, 'Não foi possível carregar as sessões.');
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.platform.revokeSession({ params: { id }, body: undefined });
      if (r.status !== 204) throw new Error('Não foi possível encerrar a sessão.');
    },
    // Guarda pelo id: se a pessoa pediu para encerrar outra linha enquanto
    // esta ainda estava em voo, a confirmação nova não é dela para apagar.
    onSettled: (_dado, error, id) =>
      setConfirming((current) => (current === id ? null : current)),
    onSuccess: () => client.invalidateQueries({ queryKey: ['plataforma', 'sessoes'] }),
  });

  return (
    <Page measure="cheia">
      <PageHeader title="Minhas sessões">
        Encerrar uma sessão vale na hora — não depende de o prazo dela acabar.
      </PageHeader>

      {/*
       * Falhar sem dado nenhum e falhar com dado velho são coisas diferentes, e
       * a frase muda porque a verdade muda: sem dado, não há lista para estar
       * "incompleta" — há a ausência dela.
       */}
      {query.isError && (
        <Alert tone="erro" title="Não foi possível carregar as sessões.">
          {query.data
            ? 'A lista abaixo é a última que carregou e pode estar desatualizada. Recarregue a página.'
            : 'Recarregue a página para tentar de novo. Nenhuma sessão foi encerrada.'}
        </Alert>
      )}
      {revoke.isError && (
        <Alert tone="erro" title="Não foi possível encerrar a sessão.">
          A sessão continua ativa. Tente de novo.
        </Alert>
      )}

      {/*
       * Erro sem dado nenhum: o aviso acima é a resposta inteira.
       *
       * Com a tabela aqui, ela anunciava "Nenhuma outra sessão ativa" logo
       * abaixo do erro — e essa é a afirmação que parece um fato. A pessoa lia
       * que não há mais nenhum aparelho conectado justamente no momento em que a
       * casa não sabe quantos há. É a mesma guarda que a tela de categorias já
       * fazia; esta não fazia.
       */}
      {(!query.isError || query.data) && (
        <Card bleed>
          <Table
            label="Meus acessos"
            items={query.data ?? []}
            loading={query.isLoading}
            rowKey={(s) => s.id}
            /*
             * A sessão ATUAL entra na lista (é ela que leva o selo "esta sessão"),
             * então esta linha não aparece para quem está logado — e por isso ela
             * não pode afirmar nada sobre os outros aparelhos. O texto anterior
             * dizia "Nenhuma outra sessão ativa", que supunha uma lista sem a
             * atual, que não é a que a API devolve.
             */
            empty="Nenhuma sessão listada."
            columns={[
              {
                title: 'Início',
                cell: (s) => (
                  <span className="flex items-center gap-2">
                    <time dateTime={s.createdAt}>
                      {format.format(new Date(s.createdAt))}
                    </time>
                    {/* A sessão que você está usando é a única que não pode ser
                      confundida com outra — então ela se identifica. */}
                    {s.current && <Badge tone="informacao">esta sessão</Badge>}
                  </span>
                ),
                minWidth: '15rem',
              },
              {
                title: 'Expira',
                cell: (s) => (
                  <time dateTime={s.expiresAt}>
                    {format.format(new Date(s.expiresAt))}
                  </time>
                ),
              },
              { title: 'IP', cell: (s) => s.ip ?? '—' },
              {
                title: 'Ação',
                hiddenLabel: 'Ação',
                alignment: 'fim',
                minWidth: '11rem',
                cell: (s) => {
                  if (s.current) return null;
                  if (confirming !== s.id) {
                    return (
                      <Button
                        variant="contorno"
                        size="sm"
                        onClick={() => setConfirming(s.id)}
                      >
                        Encerrar
                      </Button>
                    );
                  }
                  return (
                    <span className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="perigo"
                        size="sm"
                        onClick={() => revoke.mutate(s.id)}
                        loading={revoke.isPending && revoke.variables === s.id}
                      >
                        Confirmar
                      </Button>
                      <Button
                        variant="discreto"
                        size="sm"
                        onClick={() => setConfirming(null)}
                        disabled={revoke.isPending}
                      >
                        Cancelar
                      </Button>
                    </span>
                  );
                },
              },
            ]}
          />
        </Card>
      )}
    </Page>
  );
}
